import { useThree } from "@react-three/fiber";
import { useEffect, useRef } from "react";
import * as THREE from "three";
import { ModelBounds } from "../../types/models";

export function CameraController({
  bounds,
  isCustomModel,
}: {
  bounds: ModelBounds | null;
  isCustomModel: boolean;
}) {
  const { camera } = useThree();
  const hasFramedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!bounds || !camera || !isCustomModel) return;

    // Use bounds center as unique identifier - only frame once per unique model
    const boundsId = `${bounds.center.x},${bounds.center.y},${bounds.center.z}`;
    if (hasFramedRef.current === boundsId) {
      return; // Already framed this model
    }

    hasFramedRef.current = boundsId;

    const box = bounds.box;
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = (camera as THREE.PerspectiveCamera).fov || 50;

    // Calculate distance needed to frame the entire model
    const vFOV = THREE.MathUtils.degToRad(fov);
    const distance = maxDim / 2 / Math.tan(vFOV / 2);

    // Position camera away from center at an angle
    const angle = Math.PI / 4; // 45 degrees
    const newPos = new THREE.Vector3(
      Math.sin(angle) * distance * 0.8,
      maxDim * 0.4,
      Math.cos(angle) * distance * 0.8,
    );
    newPos.add(bounds.center);

    camera.position.copy(newPos);
    camera.updateProjectionMatrix();
  }, [bounds, isCustomModel, camera]);

  return null;
}
